//
// # Layer Utilities
//
// Shared layer scanning and grouping functions used by both the
// EditorCore overlay (LayerInfo) and the IDE sidebar panel (LayerPanel).
//

import type { LayoutRender } from "./content/layout/render";
import { schema, pdk_utils } from "GenAlphaSchema";
import { layout } from "GenAlphaModel";

/**
 * Get layers to display by scanning the layout for actually used layers.
 * Combines:
 * 1. Layers with elements in the current layout (decoded from encoded values)
 * 2. Stack metal/via layers (for routing planning, even if empty) - unless usedOnly is true
 *
 * @param usedOnly - If true, only return layers that have actual shapes (skip empty stack layers)
 */
export function getLayersToDisplay(
  pdk: schema.pdk.PDK,
  stack: schema.pdk.Stack | undefined,
  layoutContent: LayoutRender,
  usedOnly?: boolean
): Array<schema.pdk.PDKLayer> {
  const usedLayerNumbers = new Set<number>();

  // Scan layout elements to find used layers
  collectUsedLayers(layoutContent, usedLayerNumbers);

  // Add stack metal/via layers (unless filtering to used-only)
  if (stack && !usedOnly) {
    for (const metalLayer of stack.metalLayers) {
      if (typeof metalLayer.layer === "number") {
        usedLayerNumbers.add(metalLayer.layer);
      }
    }
    for (const via of stack.vias) {
      if (typeof via.layer === "number") {
        usedLayerNumbers.add(via.layer);
      }
    }
  }

  // Look up PDK layers for the used layer numbers
  const pdkLayerMap = new Map<number, schema.pdk.PDKLayer>();
  for (const layer of pdk.layers) {
    pdkLayerMap.set(layer.number, layer);
  }

  // Return PDK layers that match used layer numbers, sorted by number.
  // For layer numbers not found in the PDK, create synthetic entries so
  // they still appear in the layer panel and can be toggled.
  const result: Array<schema.pdk.PDKLayer> = [];
  for (const layerNum of usedLayerNumbers) {
    const pdkLayer = pdkLayerMap.get(layerNum);
    if (pdkLayer) {
      result.push(pdkLayer);
    } else {
      // Synthetic entry for a layer not defined in the PDK
      result.push({
        number: layerNum,
        name: `Layer ${layerNum}`,
        purposes: [],
        category: "other",
      });
    }
  }

  return result.sort((a, b) => a.number - b.number);
}

/**
 * Recursively collect layer numbers from layout elements.
 * Decodes encoded layer values (layer * 65536 + purpose) to extract layer numbers.
 */
function collectUsedLayers(
  layoutContent: LayoutRender,
  usedLayers: Set<number>
): void {
  const impl = layoutContent.layoutEntity.implEntity?.data;
  if (!impl) return;

  // Collect from top-level elements
  for (const elem of impl.elems) {
    const { layer: layerNum } = pdk_utils.decodeLayer(elem.layer);
    usedLayers.add(layerNum);
  }

  // Collect from routes
  for (const route of impl.routes) {
    for (const point of route.path) {
      usedLayers.add(point.layer.layer);
    }
  }

  // Recursively collect from instances
  for (const instance of impl.instances) {
    collectLayersFromInstance(instance, usedLayers);
  }
}

/**
 * Collect layers from a layout's impl: scan its elements and recurse into instances.
 */
function collectLayersFromImpl(
  implLayout: layout.Layout,
  usedLayers: Set<number>
): void {
  if (!implLayout.impl) return;

  for (const elem of implLayout.impl.elems) {
    const { layer: layerNum } = pdk_utils.decodeLayer(elem.layer);
    usedLayers.add(layerNum);
  }

  // Collect from routes in this impl
  for (const route of implLayout.impl.routes) {
    for (const point of route.path) {
      usedLayers.add(point.layer.layer);
    }
  }

  for (const nestedInstance of implLayout.impl.instances) {
    collectLayersFromInstance(nestedInstance, usedLayers);
  }
}

/**
 * Collect layers from an instance's target layout.
 * Uses the model's Instance type which has BoundTarget with resolved layout.
 *
 * For segmented instances, also scans per-segment target overrides. The renderer
 * draws per-segment targets directly (e.g. abutment variants with dummy gates),
 * so their layers must be included here too or they cannot be toggled in the panel.
 */
function collectLayersFromInstance(
  instance: layout.Instance,
  usedLayers: Set<number>
): void {
  const { target } = instance;
  if (target.kind !== "bound") return;

  collectLayersFromImpl(target.layout, usedLayers);

  // Scan per-segment target overrides (segmented placement only).
  // Each segment may override the instance-level target with a variant cell
  // (e.g. a transistor with dummy gates on a specific side). The base target
  // above may not contain those layers at all.
  if (instance.place?.kind === "segments") {
    for (const seg of instance.place.segments) {
      if (seg?.target) {
        const segLayout = layout.Layout.layouts.get(seg.target);
        if (segLayout) {
          collectLayersFromImpl(segLayout, usedLayers);
        }
      }
    }
  }
}

// Group layers by category for organized display
export function groupLayersByCategory(
  layers: Array<schema.pdk.PDKLayer>
): Map<string, Array<schema.pdk.PDKLayer>> {
  const categoryMap = new Map<string, Array<schema.pdk.PDKLayer>>();

  for (const layer of layers) {
    const category = layer.category;
    const categoryLayers = categoryMap.get(category) ?? [];
    categoryLayers.push(layer);
    categoryMap.set(category, categoryLayers);
  }

  // Sort categories: metal, via, then others alphabetically
  const sortedCategories = Array.from(categoryMap.entries()).sort(
    ([catA], [catB]) => {
      const order = ["metal", "via"];
      const indexA = order.indexOf(catA);
      const indexB = order.indexOf(catB);
      if (indexA !== -1 && indexB !== -1) return indexA - indexB;
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      return catA.localeCompare(catB);
    }
  );

  return new Map(sortedCategories);
}
